import { isAdminFirestoreAdapter, isClientFirestoreAdapter } from "./guards";
// NOTE: May be useful in the future:
// export type OmitFieldValues<F extends FirestoreConnection, DbModel> = {
//     [Property in keyof DbModel]: Property extends FieldValue<F> ? never : DbModel[Property];
// };
/**
 * Returns the FieldValue.delete() sentinel value for the given Firestore adapter's connection type
 *
 * We are using dynamic imports here, to avoid needing to import the whole of firebase-admin in the client or the client
 * SDK from the backend. We'd usually be able to avoid that via the use of `import type` (which disappears at runtime),
 * but FieldValues are sentinel values, not types.
 *
 * It's very important that the dynamic imports exactly match the rest of the client. e.g. trying to use require() to
 * avoid the need for a promise doesn't work, because it loads a parallel entire firebase namespace.
 */
export async function firestoreFieldValueDelete(adapter) {
    if (isClientFirestoreAdapter(adapter)) {
        await import("firebase/compat/firestore"); // For side-effect
        return (await import("firebase/compat/app")).default.firestore.FieldValue.delete();
    }
    if (isAdminFirestoreAdapter(adapter)) {
        return (await import("firebase-admin")).default.firestore.FieldValue.delete();
    }
    throw new Error("Unknown connection type");
}
/**
 * Returns the Firestore `FieldValue.increment()` sentinel value for the specified Firestore adapter's connection type.
 *
 * We are using dynamic imports here, to avoid needing to import the whole of firebase-admin in the client or the client
 * SDK from the backend. We'd usually be able to avoid that via the use of `import type` (which disappears at runtime),
 * but FieldValues are sentinel values, not types.
 *
 * It's very important that the dynamic imports exactly match the rest of the client. e.g. trying to use require() to
 * avoid the need for a promise doesn't work, because it loads a parallel entire firebase namespace.
 */
export async function firestoreFieldValueIncrement(adapter, count = 1) {
    if (isClientFirestoreAdapter(adapter)) {
        await import("firebase/compat/firestore"); // For side-effect
        return (await import("firebase/compat/app")).default.firestore.FieldValue.increment(count);
    }
    if (isAdminFirestoreAdapter(adapter)) {
        return (await import("firebase-admin")).default.firestore.FieldValue.increment(count);
    }
    throw new Error("Unknown connection type");
}
/**
 * Returns the FieldValue.arrayUnion(...v[]) function for the given Firestore adapter's connection type
 *
 * We are using dynamic imports here, to avoid needing to import the whole of firebase-admin in the client or the client
 * SDK from the backend. We'd usually be able to avoid that via the use of `import type` (which disappears at runtime),
 * but FieldValues are sentinel values, not types.
 *
 * It's very important that the dynamic imports exactly match the rest of the client. e.g. trying to use require() to
 * avoid the need for a promise doesn't work, because it loads a parallel entire firebase namespace.
 */
export async function firestoreFieldValueArrayUnion(adapter) {
    if (isClientFirestoreAdapter(adapter)) {
        await import("firebase/compat/firestore"); // For side-effect
        return (await import("firebase/compat/app")).default.firestore.FieldValue.arrayUnion;
    }
    if (isAdminFirestoreAdapter(adapter)) {
        return (await import("firebase-admin")).default.firestore.FieldValue.arrayUnion;
    }
    throw new Error("Unknown connection type");
}
/**
 * Returns the FieldValue.arrayRemove(v) function for the given Firestore adapter's connection type
 *
 * We are using dynamic imports here, to avoid needing to import the whole of firebase-admin in the client or the client
 * SDK from the backend. We'd usually be able to avoid that via the use of `import type` (which disappears at runtime),
 * but FieldValues are sentinel values, not types.
 *
 * It's very important that the dynamic imports exactly match the rest of the client. e.g. trying to use require() to
 * avoid the need for a promise doesn't work, because it loads a parallel entire firebase namespace.
 */
export async function firestoreFieldValueArrayRemove(adapter) {
    if (isClientFirestoreAdapter(adapter)) {
        await import("firebase/compat/firestore"); // For side-effect
        return (await import("firebase/compat/app")).default.firestore.FieldValue.arrayRemove;
    }
    if (isAdminFirestoreAdapter(adapter)) {
        return (await import("firebase-admin")).default.firestore.FieldValue.arrayRemove;
    }
    throw new Error("Unknown connection type");
}
/**
 * Returns a FieldPath object for the given Firestore adapter's connection type.
 *
 * This is useful for nested paths that may contain special characters like a dot "." in an email address.
 *
 * We are using dynamic imports here, to avoid needing to import the whole of firebase-admin in the client or the client
 * SDK from the backend. We'd usually be able to avoid that via the use of `import type` (which disappears at runtime),
 * but FieldValues are sentinel values, not types.
 *
 * It's very important that the dynamic imports exactly match the rest of the client. e.g. trying to use require() to
 * avoid the need for a promise doesn't work, because it loads a parallel entire firebase namespace.
 */
export async function firestoreFieldPath(adapter, ...fieldNames) {
    if (isClientFirestoreAdapter(adapter)) {
        await import("firebase/compat/firestore"); // For side-effect
        return new (await import("firebase/compat/app")).default.firestore.FieldPath(...fieldNames);
    }
    if (isAdminFirestoreAdapter(adapter)) {
        return new (await import("firebase-admin")).default.firestore.FieldPath(...fieldNames);
    }
    throw new Error("Unknown connection type");
}
//# sourceMappingURL=fieldValue.js.map